import React from 'react';
import {useSize} from '@packages/utilities/styled';

import Divider from '../../Divider';
import Portal from '../../Portal';
import {OptionType} from '../../SearchableList';

import {
  SearchableListItemProps,
  SearchableListItemWrapper,
} from '../../SearchableList/SearchableListItem/SearchableListItem';
import {useSearchableListItemContext} from '../../SearchableList/SearchableListItem/useSearchableListItem';

import {AutocompleteOptions, AutocompleteProps} from '../_types';
import {useAutocompleteListBoxContext} from '../AutocompleteListBox/useAutocompleteListBoxContext';

import {AutocompleteItem, AutocompleteItemBody} from './AutocompleteItem';

const CreatableOption = <TOptions extends AutocompleteOptions>({
  option,
}: {
  option: Parameters<
    NonNullable<AutocompleteProps<TOptions>['renderOptionLabel']>
  >[0];
}) => {
  const {label, prefix, value} = option;

  const {isActive} = useSearchableListItemContext();
  const {activeOptionNote, setActiveOptionNote} =
    useAutocompleteListBoxContext();

  React.useEffect(() => {
    if (isActive && activeOptionNote) {
      setActiveOptionNote(null);
    }
  }, [isActive, activeOptionNote, setActiveOptionNote]);

  return (
    <AutocompleteItem checkmarkStyle="none" key={value} prefix={prefix}>
      <AutocompleteItemBody>{label}</AutocompleteItemBody>
    </AutocompleteItem>
  );
};

type AutocompleteCreatableOptionProps = {
  density: SearchableListItemProps['density'];
  portalRoot: HTMLDivElement | null;
  option: OptionType;
  shouldRenderDivider: boolean;
  verticalItemAlignment: SearchableListItemProps['verticalItemAlignment'];
  onMount?: () => void;
  onUnmount?: () => void;
};

export const AutocompleteCreatableOption = ({
  density,
  portalRoot,
  option,
  shouldRenderDivider,
  verticalItemAlignment,
  onMount,
  onUnmount,
}: AutocompleteCreatableOptionProps) => {
  const size = useSize();

  React.useEffect(() => {
    onMount?.();

    return () => {
      onUnmount?.();
    };
  }, []);

  const renderCreatableOption = React.useCallback(() => {
    // @ts-expect-error - Need to figure out how to best handle these types. I believe it is due to the optional options prop, but not entirely sure yet. Once we resolve that the discriminated union type funkiness will probably return.
    return <CreatableOption option={option} />;
  }, [option]);

  return (
    <Portal key={option.value} root={portalRoot}>
      {shouldRenderDivider && <Divider spacing={size === 'x-large' ? 2 : 1} />}
      <SearchableListItemWrapper
        density={density}
        key={option.value}
        option={option}
        renderOption={renderCreatableOption}
        verticalItemAlignment={verticalItemAlignment}
      />
    </Portal>
  );
};
